/**
 * planStagingObjects — the entry point a caller persists.
 *
 * STRIP ORDER IS LOAD-BEARING. Strip the previous run's props FIRST, then plan
 * against the stripped room. Planning first and stripping after is the bug
 * dorm shipped and had to fix: the placers treat everything already in the
 * room as an obstacle, so last run's tray blocks this run's tray, nothing is
 * emitted, and the strip then deletes the old one. The room shows decor in the
 * preview and loses it on save. Do not reorder.
 *
 * Re-staging is therefore idempotent by RE-PLANNING, not by diffing: the props
 * are regenerated against the room's current furniture rather than preserved
 * where they were. That is what makes "run it again after the layout changed"
 * a single call.
 */

const { isStagingDecor, STAGING_TOOL } = require('./ownership');
const { resolveStagingStyle } = require('./stagingStyles');
const { planBathroomStaging } = require('./plan');
const { planKitchenStaging } = require('./plan/kitchen');
const { stagingKindOf } = require('./stagingKind');

/**
 * @param {Object} room  a hydrated room (real opening geometry, not slim copies)
 * @param {Object|string} style  a resolved pack, or a style id
 * @param {Object} [opts]
 * @param {boolean} [opts.persistIds]  mint fresh ids (save) vs keep planner-local (preview)
 * @param {() => string} [opts.freshId]
 * @param {string} [opts.generatedAt]  ISO stamp for provenance
 * @returns {{ decor: Object[], objects: Object[] }}
 *   `decor` is what a live preview renders; `objects` is the room's full
 *   replacement array.
 */
/**
 * Re-mint every entry's id, carrying its `parents` reference with it.
 *
 * The props standing ON the tray are parented to the tray by its
 * planner-local id (`staging-staging-tray-4`, minted in `plan/index.js`).
 * Re-minting ids without rewriting those references left the candle and the
 * jar pointing at an id that no longer existed -- 32 of 119 links across the
 * corpus. `parents` is what makes `moveGroup` drag them when the tray moves
 * and `moveUnit` carry them through a collision nudge, and BOTH fail
 * silently on a dangling id (neither throws, neither logs, the prop is just
 * never admitted to the group). So the tray's own props were the ones it
 * could not carry.
 *
 * Two passes, because an entry's parent may be re-minted after it. A parent
 * the map does not know is left alone: that is a real scanned `countertop`,
 * whose id this never touches.
 */
function repointToFreshIds(decor, freshId) {
  const remap = new Map();
  const reIded = decor.map((entry) => {
    const id = freshId();
    if (entry.id) remap.set(entry.id, id);
    return { ...entry, id };
  });
  return reIded.map((entry) =>
    Array.isArray(entry.parents) && entry.parents.some((p) => remap.has(p?.id))
      ? {
          ...entry,
          parents: entry.parents.map((p) =>
            remap.has(p?.id) ? { ...p, id: remap.get(p.id) } : p
          ),
        }
      : entry
  );
}

function planStagingObjects(room, style, opts = {}) {
  const resolved =
    typeof style === 'string' || style == null
      ? resolveStagingStyle(style)
      : style;

  const kept = (room?.objects || [])
    .filter(Boolean)
    .filter((o) => !isStagingDecor(o));
  const strippedRoom = { ...room, objects: kept };

  const generatedAt = opts.generatedAt || new Date().toISOString();
  // A kitchen gets the kitchen planner. Everything else — a bathroom, and any
  // room no stager claims — keeps the bathroom planner it always had, so the
  // output for those rooms is unchanged (pinned by staging-golden.ci.test.js).
  const planner =
    stagingKindOf(strippedRoom) === 'kitchen'
      ? planKitchenStaging
      : planBathroomStaging;
  let decor = planner(strippedRoom, resolved, {
    idPrefix: opts.idPrefix,
    generatedAt,
  });

  if (opts.persistIds && typeof opts.freshId === 'function') {
    decor = repointToFreshIds(decor, opts.freshId);
  }

  return { decor, objects: [...kept, ...decor] };
}

module.exports = { planStagingObjects, STAGING_TOOL };
